import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../icons/Icon';
import { demoLeagueMatches, TEAMS_BY_ID, LEAGUES_BY_ID } from '../../services/demoData';
import './GlobalSearch.css';

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ teams: [], leagues: [], matches: [] });
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Search demo data on query change
  useEffect(() => {
    if (!query.trim()) {
      setResults({ teams: [], leagues: [], matches: [] });
      return;
    }
    const q = query.toLowerCase();

    const teams = Object.values(TEAMS_BY_ID)
      .filter(t => t.name.toLowerCase().includes(q) || t.short.toLowerCase().includes(q))
      .slice(0, 5);

    const leagues = Object.values(LEAGUES_BY_ID)
      .filter(l => l.name.toLowerCase().includes(q) || l.country.toLowerCase().includes(q))
      .slice(0, 4);

    const matches = [];
    demoLeagueMatches.leagues.forEach(league => {
      league.matches.forEach(m => {
        if (
          m.teams.home.name.toLowerCase().includes(q) ||
          m.teams.away.name.toLowerCase().includes(q) ||
          league.name.toLowerCase().includes(q)
        ) {
          matches.push(m);
        }
      });
    });

    setResults({ teams, leagues, matches: matches.slice(0, 5) });
  }, [query]);

  // Auto-focus when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  // Escape closes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setQuery('');
  };

  const handleMatchClick = (id) => {
    navigate(`/match/${id}`);
    handleClose();
  };

  const handleLeagueClick = () => {
    navigate('/standings');
    handleClose();
  };

  const hasResults = results.teams.length || results.leagues.length || results.matches.length;

  return (
    <>
      <button
        className="search-icon-btn"
        onClick={() => setOpen(true)}
        aria-label="Search"
        title="Search"
      >
        <Icon name="search" size={20} />
      </button>

      {open && (
        <div className="search-overlay" onClick={handleClose}>
          <div className="search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-modal-input-row">
              <Icon name="search" size={18} className="search-modal-icon" />
              <input
                ref={inputRef}
                type="text"
                className="search-modal-input"
                placeholder="Search teams, leagues, matches…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button className="search-close-btn" onClick={handleClose} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {query && (
              <div className="search-modal-results">
                {!hasResults && (
                  <div className="search-empty">No results for "{query}"</div>
                )}

                {results.matches.length > 0 && (
                  <div className="search-section">
                    <div className="search-section-title">Matches</div>
                    {results.matches.map(m => (
                      <button key={m.id} className="search-result" onClick={() => handleMatchClick(m.id)}>
                        <div className="search-match">
                          <img src={m.teams.home.logo} alt="" />
                          <span>{m.teams.home.short}</span>
                          <span className="search-vs">vs</span>
                          <span>{m.teams.away.short}</span>
                          <img src={m.teams.away.logo} alt="" />
                        </div>
                        <span className="search-meta">{m.league.name}</span>
                      </button>
                    ))}
                  </div>
                )}

                {results.teams.length > 0 && (
                  <div className="search-section">
                    <div className="search-section-title">Teams</div>
                    {results.teams.map(t => (
                      <button key={t.id} className="search-result" onClick={handleClose}>
                        <img src={t.logo} alt="" />
                        <span>{t.name}</span>
                      </button>
                    ))}
                  </div>
                )}

                {results.leagues.length > 0 && (
                  <div className="search-section">
                    <div className="search-section-title">Leagues</div>
                    {results.leagues.map(l => (
                      <button key={l.id} className="search-result" onClick={handleLeagueClick}>
                        <img src={l.logo} alt="" />
                        <span>{l.name}</span>
                        <span className="search-meta">{l.country}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
